<script setup lang="ts">
import type { PaAPI, PageFilter } from '@rialtic/types'
import { FilterNames } from '@rialtic/types'
import type { Row } from '@tanstack/vue-table'
import { createColumnHelper } from '@tanstack/vue-table'
import { del } from 'idb-keyval'
import { isEqual } from 'ohash'
import {
  ALL_LOB,
  exclusionFilterAnalyticsTagMap,
  sortColumnMapExclusions,
} from '~/constants'
import { useWorkspace } from '~/stores/workspace'
import { getExclusionsBasePath } from '~/utils/exclusionsRoute'
import { getExclusionStatus } from '~/utils/exclusionStatus'
import type { TableView } from '~/components/DataGridToolbar.vue'

definePageMeta({
  title: 'Exclusions',
  middleware: 'connector-required',
  defaultQuery: {
    [FilterNames.SORT_BY]: `${sortColumnMapExclusions.max_savings_opportunity_active},1,desc`,
    status: 'Running|Stopped|Draft',
    view: 'analytics',
  },
})

export type ExclusionTableRow =
  PaAPI.Schemas['ExclusionWithRelationsResponse'] & {
    status?: string
    statusLabel?: string
    isSubRow?: boolean
    connector_id?: string
    link?: string
    dateStart?: string | null
    dateEnd?: string | null
    category_name?: string
    effective_by?: string
    effective_date_start?: string | null
    effective_date_end?: string | null
    impacted_claim_count?: string
    impacted_claim_count_active?: string
    impacted_claim_count_observation?: string
    min_savings_opportunity?: string
    max_savings_opportunity?: string
    min_savings_opportunity_active?: string
    max_savings_opportunity_active?: string
    min_savings_opportunity_observation?: string
    max_savings_opportunity_observation?: string
    impact?: PaAPI.Schemas['HiroExclusionImpact']
    exclusion_version_connectors?: PaAPI.Schemas['ExclusionVersionConnectorWithRelationsResponse'][]
  }

const DEBOUNCE_TIMER = <const>{ debounce: 600 }

const { $toast, $pendo } = useNuxtApp()

const analyticsTag = (tag: string) =>
  `${tag}:${workspace.connectorId === ALL_LOB ? ALL_LOB : 'single_lob'}`

const exclusionAnalyticsTagMap = computed(() =>
  Object.fromEntries(
    Object.entries(exclusionFilterAnalyticsTagMap).map(([key, tag]) => [
      key,
      analyticsTag(tag),
    ]),
  ),
)

const route = useRoute<'exclusions' | 'exclusions-id'>()

const router = useRouter()
const routeId = computed(() =>
  'id' in route.params ? String(route.params.id) : undefined,
)

const workspace = useWorkspace()

const { normalizedParams, activeUrlParams, clearUrlParams, setUrlParams } =
  useUrlParamsState()

const getExclusionRouteQuery = () => ({
  ...route.query,
  lob: workspace.connectorId === ALL_LOB ? ALL_LOB : workspace.connectorId,
})

if (import.meta.client && !activeUrlParams.status) {
  setUrlParams('status', 'Running|Stopped|Draft')
}

const { isNavigating } = useNavigation()

// Get sidebar state for dynamic layout
const { sideNavMini } = useLayoutState()

const { buildExportedFilters, hasActiveFilters } = useFiltersState()

const firstDataLoaded = ref(false)

const { state: sortState, urlSortState } = useUrlSortState(
  sortColumnMapExclusions,
)

const { data: filterData } = useApiFetch<
  PaAPI.Get['/connector/{connector_id}/exclusion/filters']
>(() => `/api/paapi/connector/${workspace.connectorId}/exclusion/filters`)

const exclusionFilters = computed(
  () => (filterData.value?.filters as PageFilter[]) ?? [],
)

const exclusionTypeNameMap: Record<string, string> = <const>{
  ALL: 'All claim lines',
  PROVIDER: 'Provider',
  GROUP_ID: 'Member group',
}

const effectiveByMap: Record<string, string> = <const>{
  DATE_OF_SERVICE: 'Date of service',
  PROCESSED_DATE: 'Processed date',
}

const search = ref(
  route.query.q && typeof route.query.q === 'string' ? route.query.q : '',
)

const debounceSearch = debouncedRef(search, 600)

const currentView = computed<string[]>(() =>
  Array.isArray(normalizedParams.value.view) ? normalizedParams.value.view : [],
)

const isViewDetails = computed(() => currentView.value.includes('details'))

const isViewAnalytics = computed(() => currentView.value.includes('analytics'))

const params = computed(() => {
  const { sort_by: _sortBy, ...filters } = normalizedParams.value

  return {
    ...filters,
    connector: workspace.connectorId,
    search_name: debounceSearch.value,
    sort: urlSortState.value ? [urlSortState.value] : undefined,
    start_date: workspace.muxFilters.start_date,
    end_date: workspace.muxFilters.end_date,
    ...(workspace.financialCalculationMethod === 'none'
      ? {}
      : {
          impact_calculation_method: workspace.financialCalculationMethod,
        }),
  }
})

const workspaceTimezone = useWorkspaceTimezone()

const {
  data: exclusions,
  status,
  refresh: refreshData,
} = useApiFetch<
  PaAPI.Schemas['ExclusionWithRelationsResponse'][],
  ExclusionTableRow[]
>('/api/paapi/exclusion', {
  params,
  transform: ((data: PaAPI.Schemas['ExclusionWithRelationsResponse'][]) => {
    // TODO: we need to think of a better way to not call this method at all
    if (isNavigating.value) return []

    if (!data) return []

    if (!firstDataLoaded.value) firstDataLoaded.value = true

    $pendo?.track('exclusions_list_displayed', {
      product_area: 'exclusions',
      connector: workspace.connectorId,
      exclusion_count: data.length,
    })

    const hasAnalyticsFailure = data.some(
      (e) =>
        (e as any).aggregate_impact?.analytics_failed ||
        e.current_exclusion_version?.exclusion_version_connectors?.some(
          (c: any) => c.impact?.analytics_failed,
        ),
    )

    if (hasAnalyticsFailure) {
      const failedConnectorNames = [
        ...new Set(
          data.flatMap((exclusion) =>
            (
              exclusion.current_exclusion_version
                ?.exclusion_version_connectors ?? []
            )
              .filter((connector) => connector.impact?.analytics_failed)
              .map(
                (connector) =>
                  connector.connector?.name || connector.connector_id,
              ),
          ),
        ),
      ]

      $toast.error(
        `Analytics failed to load${failedConnectorNames.length ? ` for ${failedConnectorNames.join(', ')}` : ''}. Reload the page to retry.`,
        {
          position: TOAST_POSITION.TOP_RIGHT,
        },
      )
    }

    return data.map((exclusion) => {
      // Add subrows from exclusion_version_connectors when in ALL_LOB mode
      const subRows =
        isAllLob.value &&
        exclusion.current_exclusion_version.exclusion_version_connectors
          ? exclusion.current_exclusion_version.exclusion_version_connectors.map(
              (
                exclusionVersionConnector: PaAPI.Schemas['ExclusionVersionConnectorWithRelationsResponse'],
              ) => {
                const impact = exclusionVersionConnector.impact

                return {
                  id: `${exclusion.id}-${exclusionVersionConnector.connector_id}`,
                  isSubRow: true,
                  name:
                    exclusionVersionConnector.connector?.name ||
                    exclusionVersionConnector.connector_id,
                  status: exclusionVersionConnector.status,
                  claim_line_type: '',
                  category_name: '',
                  connector_id: exclusionVersionConnector.connector_id,
                  impact,
                  impacted_claim_count: formatCount(
                    impact?.impacted_claim_count,
                  ),
                  impacted_claim_count_active: formatCount(
                    impact?.impacted_claim_count_active,
                  ),
                  impacted_claim_count_observation: formatCount(
                    impact?.impacted_claims_observation,
                  ),
                  min_savings_opportunity: formatCents(
                    impact?.min_savings_opportunity,
                    NumeralFormats.DETAIL_DOLLARS_WITHOUT_CENTS,
                  ),
                  max_savings_opportunity: formatCents(
                    impact?.max_savings_opportunity,
                    NumeralFormats.DETAIL_DOLLARS_WITHOUT_CENTS,
                  ),
                  min_savings_opportunity_active: formatCents(
                    impact?.min_savings_opportunity_active,
                    NumeralFormats.DETAIL_DOLLARS_WITHOUT_CENTS,
                  ),
                  max_savings_opportunity_active: formatCents(
                    impact?.max_savings_opportunity_active,
                    NumeralFormats.DETAIL_DOLLARS_WITHOUT_CENTS,
                  ),
                  min_savings_opportunity_observation: formatCents(
                    impact?.min_savings_opportunity_observation,
                    NumeralFormats.DETAIL_DOLLARS_WITHOUT_CENTS,
                  ),
                  max_savings_opportunity_observation: formatCents(
                    impact?.max_savings_opportunity_observation,
                    NumeralFormats.DETAIL_DOLLARS_WITHOUT_CENTS,
                  ),
                }
              },
            )
          : undefined

      const { id, name, claim_line_type, current_exclusion_version, category } =
        exclusion

      const { effective_date_start, effective_date_end, effective_by } =
        current_exclusion_version

      const status = getExclusionStatus(exclusion, workspace.connectorId)

      const baseViewData = {
        status,
        statusLabel: status === 'Stopped' ? 'Not Running' : status,
        name,
        id,
      }

      let viewData: Record<string, unknown> = {
        ...baseViewData,
        link: `${location.origin}${getExclusionsBasePath(workspace.connectorId)}/${id}?lob=${getExclusionRouteQuery().lob}`,
        subRows,
      }

      if (isViewDetails.value) {
        viewData = {
          ...viewData,
          claim_line_type,
          category_name: category?.name || '-',
          dateEnd: effective_date_end
            ? workspaceTimezone.format(
                effective_date_end,
                intlDateFormats.monDayYear,
              )
            : null,
          dateStart: effective_date_start
            ? workspaceTimezone.format(
                effective_date_start,
                intlDateFormats.monDayYear,
              )
            : null,
          effective_by: effective_by ? effectiveByMap[effective_by] : '-',
        }
      }

      if (isViewAnalytics.value) {
        const impact = isAllLob.value
          ? (
              exclusion as {
                aggregate_impact?: PaAPI.Schemas['HiroExclusionImpact']
              }
            ).aggregate_impact
          : current_exclusion_version.exclusion_version_connectors.find(
              (connector: any) =>
                connector.connector_id === workspace.connectorId,
            )?.impact

        viewData = {
          ...viewData,
          impact,
          impacted_claim_count: formatCount(impact?.impacted_claim_count),
          impacted_claim_count_active: formatCount(
            impact?.impacted_claim_count_active,
          ),
          impacted_claim_count_observation: formatCount(
            impact?.impacted_claims_observation,
          ),
          min_savings_opportunity: formatCents(
            impact?.min_savings_opportunity,
            NumeralFormats.DETAIL_DOLLARS_WITHOUT_CENTS,
          ),
          max_savings_opportunity: formatCents(
            impact?.max_savings_opportunity,
            NumeralFormats.DETAIL_DOLLARS_WITHOUT_CENTS,
          ),
          min_savings_opportunity_active: formatCents(
            impact?.min_savings_opportunity_active,
            NumeralFormats.DETAIL_DOLLARS_WITHOUT_CENTS,
          ),
          max_savings_opportunity_active: formatCents(
            impact?.max_savings_opportunity_active,
            NumeralFormats.DETAIL_DOLLARS_WITHOUT_CENTS,
          ),
          min_savings_opportunity_observation: formatCents(
            impact?.min_savings_opportunity_observation,
            NumeralFormats.DETAIL_DOLLARS_WITHOUT_CENTS,
          ),
          max_savings_opportunity_observation: formatCents(
            impact?.max_savings_opportunity_observation,
            NumeralFormats.DETAIL_DOLLARS_WITHOUT_CENTS,
          ),
        }
      }

      return viewData
    })
  }) as any,
  watch: false,
})

const clearAllFilters = () => {
  clearUrlParams()
  search.value = ''
}

const noResults = computed(() => {
  if (!firstDataLoaded.value) return false

  if (exclusions.value?.length || status.value === 'pending') return false

  return !!(Object.keys(activeUrlParams).length || search.value)
})

const toggleView = (viewName: 'analytics' | 'details') => {
  if (currentView.value.includes(viewName)) {
    setUrlParams(
      'view',
      currentView.value.filter((view) => view !== viewName),
    )
  } else {
    setUrlParams('view', [...currentView.value, viewName])
  }
}

const pageHeader = useTemplateRef<HTMLElement>('pageHeader')

const { height } = useElementBounding(pageHeader, { windowScroll: false })

const { height: windowHeight } = useWindowSize()

// 64 is the height of the app toolbar
// 60 is the height of the header with h1
const gridHeight = computed(() => windowHeight.value - height.value - 64 - 60)

const isAllLob = computed(() => workspace.connectorId === ALL_LOB)

const onRowClick = (row: Row<ExclusionTableRow>) => {
  const isSubRow = row.original.isSubRow

  const exclusionId = isSubRow
    ? row.getParentRow()?.original.id
    : row.original.id

  const query = isSubRow
    ? { ...getExclusionRouteQuery(), lob: row.original.connector_id }
    : getExclusionRouteQuery()

  router.push({
    path: `${getExclusionsBasePath(workspace.connectorId)}/${exclusionId}`,
    query,
  })
}

const onNextOffset = () => {
  // TODO:handle pagination
}

const TABLE_BORDER_STYLE = 'border-l border-neutral-200'

const ExclusionStatusBadge = resolveComponent('ExclusionStatusBadge')

const renderAnalyticsCell = (
  value: string | number | undefined,
  hasError: boolean,
) =>
  h(
    'span',
    {
      class: {
        'font-fixed': true,
        'text-red-700': hasError,
        'h-6.5': true,
        flex: true,
        'items-center': true,
        'justify-end': true, // Right-aligned content since these are numeric
      },
    },
    hasError ? '–' : value,
  )

const columnHelper = createColumnHelper<ExclusionTableRow>()

const { width: windowWidth } = useWindowSize()

const columns = computed(() => {
  // Make the Name column dynamically fill the remaining viewport width
  const statusSize = 168
  const MINIMUM_NAME_SIZE = 300
  // When sidebar is mini (collapsed), add the extra space (256px - 72px = 184px) to name column
  const sidebarSpaceGained = sideNavMini.value ? 184 : 0
  const expanderWidth = isAllLob.value ? 40 : 0
  const analyticsColumnsWidth = isViewAnalytics.value ? 6 * 152 : 0

  const detailsColumnsWidth = isViewDetails.value
    ? 152 + 150 + 152 + 152 + 160
    : 0

  const fixedColumnsWidth =
    statusSize + expanderWidth + analyticsColumnsWidth + detailsColumnsWidth

  const nameSize = Math.max(
    MINIMUM_NAME_SIZE,
    windowWidth.value - fixedColumnsWidth - 200 + sidebarSpaceGained, // 200 buffer for padding/scrollbar
  )

  const expanderColumn = columnHelper.display({
    id: 'expander',
    header: () => null,
    cell: ({ row }) =>
      row.depth === 0
        ? h(
            'div',
            {
              class: 'flex items-center',
              onClick: (e: Event) => {
                e.stopPropagation()
                row.getToggleExpandedHandler()?.()
              },
              style: {
                cursor: 'pointer',
                paddingLeft: `${row.depth * 20}px`,
              },
            },
            [
              h('div', {
                class: `i-ph-caret-right-bold h-4 w-4 transition-transform ${row.getIsExpanded() ? 'rotate-90' : ''}`,
              }),
            ],
          )
        : null,
    size: 50,
    enableResizing: false,
    meta: createColumnMeta({
      align: 'left',
    }),
  })

  return [
    ...(isAllLob.value ? [expanderColumn] : []),
    columnHelper.group({
      id: 'exclusion-group',
      header: 'Exclusion',
      columns: [
        // ...(isAllLob.value ? [expanderColumn] : []),
        columnHelper.accessor('status', {
          cell: (props) => {
            const isSubRow = props.row.original.isSubRow
            const status = props.getValue()

            const hideBadge =
              isAllLob.value &&
              isSubRow &&
              props.row.original.status?.includes('Draft')

            return h(
              'div',
              { class: 'h-6.5 flex items-center justify-start' },
              hideBadge
                ? []
                : h(ExclusionStatusBadge, {
                    status,
                    initials: isAllLob.value && !isSubRow,
                  }),
            )
          },
          size: 168,
          enableResizing: false,
          header: 'Status',
          meta: createColumnMeta({ align: 'left' }),
        }),
        columnHelper.accessor('name', {
          header: 'Name',
          size: nameSize,
          minSize: 200,
          cell: (props) => {
            const isSubRow = props.row.original.isSubRow

            return h(
              'span',
              {
                class: isSubRow
                  ? 'font-fixed text-sm text-neutral-600'
                  : 'group-hover:underline font-medium font-display cursor-pointer',
              },
              props.getValue(),
            )
          },
          meta: createColumnMeta({
            className:
              'group-hover:underline font-semibold font-fixed cursor-pointer',
            align: 'left',
          }),
        }),
      ],
    }),
    columnHelper.group({
      id: 'analytics-active-group',
      header: 'Active',
      meta: createColumnMeta({
        className: TABLE_BORDER_STYLE,
      }),
      columns: [
        columnHelper.accessor('impact.impacted_claim_count_active', {
          id: 'impacted_claim_count_active',
          cell: (props) =>
            renderAnalyticsCell(
              props.row.original?.impacted_claim_count_active,
              Boolean(props.row.original?.impact?.analytics_failed),
            ),
          enableResizing: false,
          header: 'Impacted Claims',
          meta: createColumnMeta({
            align: 'right',
            className: TABLE_BORDER_STYLE,
          }),
          size: 152,
          sortDescFirst: true,
          sortingFn: 'basic',
        }),
        columnHelper.accessor('impact.min_savings_opportunity_active', {
          id: 'min_savings_opportunity_active',
          cell: (props) =>
            renderAnalyticsCell(
              props.row.original?.min_savings_opportunity_active,
              Boolean(props.row.original?.impact?.analytics_failed),
            ),
          enableResizing: false,
          header: 'Est. Impact (Low)',
          meta: createColumnMeta({ align: 'right' }),
          size: 152,
          sortDescFirst: true,
          sortingFn: 'basic',
        }),
        columnHelper.accessor('impact.max_savings_opportunity_active', {
          id: 'max_savings_opportunity_active',
          cell: (props) =>
            renderAnalyticsCell(
              props.row.original?.max_savings_opportunity_active,
              Boolean(props.row.original?.impact?.analytics_failed),
            ),
          enableResizing: false,

          header: 'Est. Impact (High)',
          meta: createColumnMeta({ align: 'right' }),
          size: 152,
          sortDescFirst: true,
          sortingFn: 'basic',
        }),
      ],
    }),
    columnHelper.group({
      id: 'analytics-observation-group',
      header: 'Observation',
      meta: createColumnMeta({
        className: TABLE_BORDER_STYLE,
      }),
      columns: [
        columnHelper.accessor('impact.impacted_claims_observation', {
          id: 'impacted_claim_count_observation',
          cell: (props) =>
            renderAnalyticsCell(
              props.row.original?.impacted_claim_count_observation,
              Boolean(props.row.original?.impact?.analytics_failed),
            ),
          enableResizing: false,
          header: 'Impacted Claims',
          meta: createColumnMeta({
            align: 'right',
            className: TABLE_BORDER_STYLE,
          }),
          size: 152,
          sortDescFirst: true,
        }),
        columnHelper.accessor('impact.min_savings_opportunity_observation', {
          id: 'min_savings_opportunity_observation',
          cell: (props) =>
            renderAnalyticsCell(
              props.row.original?.min_savings_opportunity_observation,
              Boolean(props.row.original?.impact?.analytics_failed),
            ),
          enableResizing: false,
          header: 'Est. Impact (Low)',
          meta: createColumnMeta({ align: 'right' }),
          size: 152,
          sortDescFirst: true,
          sortingFn: 'basic',
        }),
        columnHelper.accessor('impact.max_savings_opportunity_observation', {
          id: 'max_savings_opportunity_observation',
          cell: (props) =>
            renderAnalyticsCell(
              props.row.original?.max_savings_opportunity_observation,
              Boolean(props.row.original?.impact?.analytics_failed),
            ),
          enableResizing: false,

          header: 'Est. Impact (High)',
          meta: createColumnMeta({ align: 'right' }),
          size: 152,
          sortDescFirst: true,
          sortingFn: 'basic',
        }),
      ],
    }),
    columnHelper.group({
      id: 'details-group',
      header: 'Details',
      enableHiding: true,
      meta: createColumnMeta({
        className: TABLE_BORDER_STYLE,
      }),
      columns: [
        columnHelper.accessor('claim_line_type', {
          id: 'claim_line_type',
          enableResizing: false,
          header: 'Exclusion Type',
          cell: (props) => {
            const value = props.getValue()

            return h(
              'span',
              { class: 'font-fixed' },
              exclusionTypeNameMap[value] || value || '–',
            )
          },
          meta: createColumnMeta({
            className: TABLE_BORDER_STYLE,
            align: 'left',
          }),
          size: 152,
        }),
        columnHelper.accessor('category_name', {
          id: 'category_name',
          enableResizing: false,
          header: 'Rationale Category',
          cell: (props) =>
            h('span', { class: 'font-fixed' }, props.getValue() || '–'),
          meta: createColumnMeta({ align: 'left' }),
        }),
        columnHelper.accessor('effective_date_start', {
          id: 'effective_date_start',
          cell: (props) =>
            h(
              'span',
              { class: 'font-fixed' },
              props.row.original?.dateStart || '–',
            ),
          enableResizing: false,
          header: 'Start Date',
          meta: createColumnMeta({ align: 'right' }),
          size: 152,
        }),
        columnHelper.accessor('effective_date_end', {
          id: 'effective_date_end',
          cell: (props) =>
            h(
              'span',
              { class: 'font-fixed' },
              props.row.original?.dateEnd || '–',
            ),
          enableResizing: false,
          header: 'End Date',
          meta: createColumnMeta({ align: 'right' }),
          size: 152,
        }),

        columnHelper.accessor('effective_by', {
          id: 'effective_by',
          enableResizing: false,
          header: 'Date Type',
          cell: (props) =>
            h('span', { class: 'font-fixed' }, props.getValue() || '–'),
          meta: createColumnMeta({ align: 'left' }),
          size: 160,
        }),
      ],
    }),
  ]
})

const columnVisibility = computed(() => {
  const showAnalytics = isViewAnalytics.value
  const showDetails = isViewDetails.value

  return {
    // analytics segmented columns
    impacted_claim_count_active: showAnalytics,
    min_savings_opportunity_active: showAnalytics,
    max_savings_opportunity_active: showAnalytics,

    impacted_claim_count_observation: showAnalytics,
    min_savings_opportunity_observation: showAnalytics,
    max_savings_opportunity_observation: showAnalytics,

    // details columns
    claim_line_type: showDetails,
    category_name: showDetails,
    effective_date_start: showDetails,
    effective_date_end: showDetails,
    effective_by: showDetails,
  }
})

// TODO: remove any cast - was `exportBindings as any` in template
const exportBindings = computed((): any => {
  const urlSearchParams = new URLSearchParams(location.search)
  urlSearchParams.set('lob', workspace.connectorId)

  const analyticsMapping = isViewAnalytics.value
    ? {
        impacted_claim_count_active: 'Active: Impacted Claims',
        min_savings_opportunity_active: 'Active: Est. Impact (Low)',
        max_savings_opportunity_active: 'Active: Est. Impact (High)',

        impacted_claim_count_observation: 'Observation: Impacted Claims',
        min_savings_opportunity_observation: 'Observation: Est. Impact (Low)',
        max_savings_opportunity_observation: 'Observation: Est. Impact (High)',
      }
    : {}

  const detailsMapping = isViewDetails.value
    ? {
        claim_line_type: 'Exclusion Type',
        category_name: 'Rationale Category',
        dateStart: 'Start Date',
        dateEnd: 'End Date',
        effective_by: 'Date Type',
      }
    : {}

  const activeFiltersForExport = buildExportedFilters(
    exclusionFilters.value,
  ).map((data, index) => {
    return {
      field: index === 0 ? 'Active Filters' : '',
      value: data.name,
      filters: data.filters,
    }
  })

  return {
    fileName: 'Exclusions',

    details: {
      data: [
        {
          field: 'Title',
          value: `Exclusions - ${formatDisplayDate(new Date())}`,
        },
        {
          field: 'Line of Business',
          value: workspace.activeConnector?.name,
        },
        {
          field: 'URL',
          value: `${location.origin}/exclusions?${urlSearchParams.toString()}`,
        },
        {
          field: 'Date Range',
          value: `${formatDisplayDate(
            workspace.selectedDateRange.start,
            'MM/dd/yy',
          )} - ${formatDisplayDate(
            workspace.selectedDateRange.end,
            'MM/dd/yy',
          )}`,
        },
        {
          field: 'Date Created',
          value: formatDisplayDate(new Date(), 'MM/dd/yy'),
        },
        ...activeFiltersForExport,
      ],
      mapping: {
        field: 'Field',
        value: 'Value',
        filters: 'Filters',
      },
      name: 'Report Details',
    },
    mapping: {
      statusLabel: 'Status',
      name: 'Name',

      ...analyticsMapping,
      ...detailsMapping,

      link: 'URL',
      rationale: 'Rationale text',
      connectorName: 'Line of Business',
      policy_id: 'Policy ID',
    },
  }
})

const resetResultData = () => {
  exclusions.value = []
}

const refreshExclusions = () => {
  resetResultData()
  refreshData()
}

debouncedWatch(
  params,
  (newParams, oldParams) => {
    const ignoredParams = {
      // params that need to be ignored
      preset: undefined,
    }

    if (
      isEqual(
        {
          ...newParams,
          ...ignoredParams,
        },
        {
          ...oldParams,
          ...ignoredParams,
        },
      )
    ) {
      return
    }

    status.value = 'pending'
    refreshExclusions()
  },
  DEBOUNCE_TIMER,
)

const tableViews = computed((): TableView<'analytics' | 'details'>[] => [
  {
    id: 'analytics',
    icon: 'i-ph-calculator',
    label: 'Analytics',
    analyticsLabel: 'table_options_analytics',
    checked: isViewAnalytics.value,
  },
  {
    id: 'details',
    icon: 'i-ph-eyeglasses',
    label: 'Details',
    analyticsLabel: 'table_options_review',
    checked: isViewDetails.value,
  },
])

const handleNewExclusion = async () => {
  // Clear any stale form data before creating a new exclusion
  await del('exclusion-form:new')

  router.push('/exclusions/create')
}
</script>

<template>
  <NuxtPage
    v-if="route.meta.overrideExclusionsPage"
    v-bind="{ exclusions: exclusions || [] }"
  />
  <main
    v-else
    class="relative flex w-full flex-1 flex-col"
    :data-analytics="analyticsTag('page-exclusions')"
  >
    <header class="flex items-center bg-white px-4 pt-1 lg:px-8">
      <div class="flex items-center">
        <h1
          class="py-3 text-lg font-semibold leading-9 text-neutral-900 lg:text-2xl"
        >
          Exclusions
        </h1>
      </div>
      <UiButton
        v-if="workspace.canManageConnector"
        name="create_exclusion"
        class="ml-auto"
        size="xs"
        :data-analytics="analyticsTag('new-exclusion-button')"
        @click="handleNewExclusion"
      >
        + New exclusion
      </UiButton>
    </header>

    <DataGridToolbar
      ref="pageHeader"
      v-model:search="search"
      :analytics-tag-map="exclusionAnalyticsTagMap"
      :collapse="Boolean(routeId)"
      :collapsible="Boolean(routeId)"
      :count="exclusions?.length ?? 0"
      :filters="exclusionFilters"
      :initial-filters="['policy', 'exclusion_type', 'date_type', 'category']"
      :plurals="{ one: 'exclusion', other: 'exclusions' }"
      :search-analytics-tag="analyticsTag('filters-search-exclusions-name')"
      :searchable-filters="['policy', 'provider_tin']"
      :views="tableViews"
      search-placeholder="Search names"
      @toggle-view="toggleView"
    >
      <template #actions>
        <ExportData
          :key="workspace.watchKey"
          v-bind="exportBindings"
          :data="exclusions || []"
          :total="exclusions?.length || 0"
          sheet-name="Exclusions"
          :data-analytics-tag="analyticsTag('export-exclusions')"
        />
      </template>
    </DataGridToolbar>
    <UiVirtualDataGrid
      v-model:sorting="sortState"
      :active-id="routeId ?? ''"
      :column-visibility="columnVisibility"
      :columns="columns as any"
      :data="(exclusions ?? []) as any"
      :has-first-data-loaded="firstDataLoaded"
      :height="gridHeight"
      :loading="status === 'pending'"
      header-type="grouped"
      manual-filtering
      manual-sorting
      remove-padding
      width="max-content"
      :data-analytics="analyticsTag('exclusions-table')"
      data-testid="exclusions_table"
      @click:row="onRowClick as any"
      @load-more="onNextOffset"
    >
      <template #append>
        <!-- TODO: fix hardcoded bool once we implement pagination -->
        <UiLoadMore
          v-if="exclusions?.length"
          :has-all-results-loaded="true"
          :is-loading="firstDataLoaded && status === 'pending'"
        />
        <div
          v-else-if="firstDataLoaded && status === 'pending'"
          class="h-60vh flex flex-1 items-center justify-center overflow-hidden transition-opacity"
        >
          <div class="flex space-x-2">
            <UiLoading class="text-secondary h-6" />
            <p class="h2 text-neutral-900">Loading...</p>
          </div>
        </div>
        <div
          v-else-if="(hasActiveFilters || search) && noResults"
          class="flex flex-1 items-center justify-center"
        >
          <div
            class="h-40vh flex flex-col items-center justify-center space-y-1 text-center"
          >
            <h2 class="h2 text-neutral-900">No data found</h2>
            <p class="body-1 pb-3 text-neutral-700">
              There's no data to display here for the selected line of business.
            </p>
            <UiButton class="mt-2" size="xs" @click="clearAllFilters">
              Reset filters
            </UiButton>
          </div>
        </div>
        <div
          v-else-if="noResults"
          class="flex flex-1 items-center justify-center"
        >
          <div
            class="h-40vh flex flex-col items-center justify-center space-y-1 text-center"
          >
            <h2 class="h2 text-neutral-900">No results</h2>
            <p class="body-1 pb-3 text-neutral-700">
              Nothing was found that matches your search.
            </p>
          </div>
        </div>
      </template>
    </UiVirtualDataGrid>

    <NuxtPage v-bind="{ exclusions: exclusions || [] }" />
  </main>
</template>
